import { Link, Navigate } from "react-router-dom";
import { AppLayout } from "../../components/layout/Header";
import DashCardPhoto from "../../components/dashboard/DashCardPhoto";
import { useAuth } from "../../js/hooks/useAuth";
import { useStore } from "../../js/state/store";
import { unacknowledgedCount } from "../../js/api/systemNotices";
import { hasClientAccess } from "../../js/auth/authService";
import { defaultPathForRole } from "../../js/auth/packageAccess";
import ReMark from "../../components/brand/ReMark";

export default function MainDashboard() {
  const { session, role } = useAuth();

  if (session?.role && session.role !== "admin") {
    return <Navigate to={defaultPathForRole(session.role)} replace />;
  }
  const { dispatch } = useStore();
  const pendingNotices = session?.userId ? unacknowledgedCount(session.userId) : 0;
  const messagesTo = hasClientAccess(role)
    ? "/client/messages"
    : role === "seller"
      ? "/seller/login"
      : "/login";

  return (
    <AppLayout>
      <div className="dashboard-frame" data-tour="main-dash">
      <section className="hero-panel" style={{ textAlign: "center" }}>
        <h1>Main Dashboard</h1>
        <p>
          Your place to continue a buyer or seller journey, review notices, and keep communication
          with your agent in one calm view.
        </p>
        {session ? (
          <p className="muted">
            Signed in as {session.name}.
            {pendingNotices ? ` ${pendingNotices} notice${pendingNotices === 1 ? "" : "s"} to acknowledge.` : ""}
          </p>
        ) : (
          <p className="muted">Use Sign in in the main navigation when you are ready to continue a saved journey.</p>
        )}
      </section>

      <div className="card-grid card-grid--3">
        <article className="card" data-tour="dash-buyer">
          <h2>Buyer’s Checklist</h2>
          <p>
            A guided walk through location, home, lifestyle, and notes — so you and your agent
            share one clear picture of the home you want.
          </p>
          <DashCardPhoto
            src="/images/dashboard/buyer-journey.png"
            alt="People reviewing home listings together on a tablet and laptop."
            mask="a"
          />
          <Link to="/start" className="primary-btn">
            Continue as a buyer
          </Link>
        </article>
        <article className="card" data-tour="dash-seller">
          <h2>Seller’s Home</h2>
          <p>
            Build a calm portfolio of features, condition, and photos so your agent can represent
            the property with care.
          </p>
          <DashCardPhoto
            src="/images/dashboard/seller-journey.png"
            alt="A homeowner and agent discussing property details at a dining table."
            mask="b"
          />
          <Link to="/seller/home" className="primary-btn">
            Continue as a seller
          </Link>
        </article>
        <article className="card" data-tour="dash-messages">
          <h2>Communications</h2>
          <p>
            Keep agent and client notes in one thread, separate from buyer and seller checklists,
            so the conversation stays with the file.
          </p>
          <DashCardPhoto
            src="/images/dashboard/messages.png"
            alt="A phone and laptop on a wooden desk showing a calm message thread."
            mask="c"
          />
          <Link to={messagesTo} className="primary-btn">
            Open messages
          </Link>
        </article>
        <article className="card">
          <h2>Notices</h2>
          <p>System updates you acknowledge — security, annual upgrades, and major releases.</p>
          <DashCardPhoto
            src="/images/dashboard/notices.png"
            alt="A person calmly reviewing a system notification on a laptop."
            mask="b"
          />
          <Link to="/notices" className="primary-btn">
            Read notices
          </Link>
        </article>
        <article className="card">
          <h2>Add to Home Screen</h2>
          <p>Keep <ReMark /> close on your phone — no extra app store step.</p>
          <DashCardPhoto
            src="/images/dashboard/add-home.png"
            alt="A hand holding a phone on the home screen, ready to keep the checklist close."
            mask="a"
          />
          <button
            type="button"
            className="ghost-btn"
            onClick={() => dispatch({ type: "OPEN_MODAL", payload: "install" })}
          >
            Show instructions
          </button>
        </article>
        <article className="card">
          <h2>Welcome</h2>
          <p>Return to the homepage anytime.</p>
          <DashCardPhoto
            src="/images/dashboard/welcome.png"
            alt="A warm home entryway with the front door opening inward."
            mask="c"
          />
          <Link to="/" className="ghost-btn">
            Return to welcome
          </Link>
        </article>
      </div>
      </div>
    </AppLayout>
  );
}
